import { Outlet, Navigate, useParams, useLocation } from 'react-router-dom'
import { useQuery } from '@apollo/client'

import { queries } from '../../graphql'
import { Container, NavTabs, Text, LinearProgress } from '../../components'
import { NoMatch } from '../noMatch'
import { useAdvisorPermissions } from '../../hooks/useAdvisorPermissions'

// Map route segments to advisor permission flags
const ROUTE_PERMISSION_MAP = {
  contribute: 'menuContribute',
  documents: 'menuDocuments',
  grant: 'menuGrants',
}

// Routes advisors can never access (no corresponding permission)
const ADVISOR_BLOCKED_ROUTES = ['share-funds-with-others', 'share-funds-with-generosity-fund']

export function Fund() {
  const { fundId } = useParams()
  const location = useLocation()
  const { isAdvisorImpersonating, ...permissions } = useAdvisorPermissions()
  const { data: { me: { fund = {} } = {} } = {}, loading } = useQuery(queries.funds.myFund, {
    skip: !fundId,
    variables: { fundId: parseInt(fundId, 10) },
  })
  if (!fundId) return <NoMatch />
  if (loading) return <LinearProgress />
  if (!fund?.id) return <NoMatch />
  if (fund?.closed)
    return <NoMatch title="Portal Closed">This portal has been closed and is no longer accessible.</NoMatch>

  // Gate advisor access based on customPermissions
  if (isAdvisorImpersonating) {
    const pathAfterFundId = location.pathname.split(`/funds/${fundId}/`)[1] || ''
    const routeSegment = pathAfterFundId.split('/')[0]

    // Block routes that advisors can never access
    if (ADVISOR_BLOCKED_ROUTES.includes(routeSegment)) {
      return <NoMatch title="Access Restricted">Your access level does not permit this action.</NoMatch>
    }

    // Check permission-gated routes
    const permFlag = ROUTE_PERMISSION_MAP[routeSegment]
    if (permFlag && !permissions[permFlag]) {
      return <NoMatch title="Access Restricted">Your access level does not permit this action.</NoMatch>
    }

    // Activity tab requires at least one sub-flag enabled
    if (routeSegment === 'activity' && !permissions.menuGivingActivity && !permissions.menuInvestmentActivity) {
      return <NoMatch title="Access Restricted">Your access level does not permit this action.</NoMatch>
    }
  }

  return <Outlet />
}

export function FundActivity() {
  const { isAdvisorImpersonating, menuGivingActivity, menuInvestmentActivity } = useAdvisorPermissions()
  const navigation = [
    (!isAdvisorImpersonating || menuGivingActivity) && {
      label: 'Giving Wallet',
      to: 'giving-wallet',
    },
    (!isAdvisorImpersonating || menuInvestmentActivity) && {
      label: 'Investment Account',
      to: 'investment-account',
    },
    { label: 'Recurring', to: 'recurring' },
  ].filter(Boolean)
  return (
    <>
      <Container maxWidth="lg" sx={{ mb: 0 }}>
        <Text.H1>Activity</Text.H1>
        <NavTabs tabs={navigation} />
      </Container>
      <Outlet />
    </>
  )
}

export function ActivityIndexRedirect() {
  const { isAdvisorImpersonating, menuGivingActivity, menuInvestmentActivity } = useAdvisorPermissions()
  if (!isAdvisorImpersonating || menuGivingActivity) {
    return <Navigate to="giving-wallet" replace />
  }
  if (menuInvestmentActivity) {
    return <Navigate to="investment-account" replace />
  }
  return <Navigate to="recurring" replace />
}

export function RedirectToDefaultFund({ using }) {
  const { generosityFundId } = useParams()
  const { data: { me: { fund } = {} } = {}, loading } = useQuery(queries.funds.myFund)
  if (loading) return <LinearProgress />
  if (!fund?.id || !generosityFundId) return <NoMatch />
  return <Navigate to={`/gift/genfund/${generosityFundId}`} replace />
}
